import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FarmDataProvider } from './context/FarmDataContext';
import { ViewComplexityProvider } from './context/ViewComplexityContext';
import { DemoModeProvider } from './context/DemoModeContext';
import { EcointelLottie, EcoIntelFullBleed } from './components/EcoIntelLoader';
import Layout from './components/Layout';
import DemoFarmRouteGuard from './components/DemoFarmRouteGuard';
import BugHerdScript from './components/BugHerdScript';
import CookieConsent from './components/CookieConsent';
import Login from './components/Login';
import { Subscription } from './components/Subscription';
import SubscriptionGate from './components/SubscriptionGate';
import Dashboard from './pages/Dashboard';
import UserProfile from './pages/UserProfile';
import Success from './pages/Success';
import Cancel from './pages/Cancel';
import Register from './pages/Register';
import AdminLayout from './components/admin/AdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import UsersAdmin from './pages/admin/UsersAdmin';
import PropertiesAdmin from './pages/admin/PropertiesAdmin';
import ReportsAdmin from './pages/admin/ReportsAdmin';
import ViewsAdmin from './pages/admin/ViewsAdmin';
import SiteContentAdmin from './pages/admin/SiteContentAdmin';
import PromoBannerAdmin from './pages/admin/PromoBannerAdmin';
import CacheAdmin from './pages/admin/CacheAdmin';
import DemoAdmin from './pages/admin/DemoAdmin';
import AnnouncementsAdmin from './pages/admin/AnnouncementsAdmin';
import EmailTemplatesAdmin from './pages/admin/EmailTemplatesAdmin';
import CredentialsVettingAdmin from './pages/admin/CredentialsVettingAdmin';
import FeedbackAdmin from './pages/admin/FeedbackAdmin';
import VerifyEmail from './pages/VerifyEmail';
import CompleteProfile from './pages/CompleteProfile';
import SetPassword from './pages/SetPassword';
import EmailLinkCallback from './pages/EmailLinkCallback';
import OnboardingGate from './components/OnboardingGate';
import StagingAccessGate from './components/StagingAccessGate';
import { ROLES } from './constants/roles';

const MapViewer = React.lazy(() => import('./components/MapViewer'));
const FarmAnalytics = React.lazy(() => import('./pages/FarmAnalytics'));
const FieldDetailsPage = React.lazy(() => import('./pages/FieldDetailsPage'));
const SuperuserDashboard = React.lazy(() => import('./pages/SuperuserDashboard'));
// v0.3 Phase 3: in-app HTML "quick reports" (Sustainability, Land Health)
// retired in favour of the subscription-packages grid in FarmReportsPage.
// The signed-PDF flow is the single source of truth for downloadable
// reports — see api.js → getReportDownloadUrl + analytics-server
// GET /api/v1/properties/:id/reports/:pkg/years/:year/download.
const FarmReportsPage = React.lazy(() => import('./pages/FarmReportsPage'));
const FarmDisclosure = React.lazy(() => import('./pages/FarmDisclosure'));
const FarmOverview = React.lazy(() => import('./pages/FarmOverview'));
const FarmAssessments = React.lazy(() => import('./pages/FarmAssessments'));
const FarmDiagnostics = React.lazy(() => import('./pages/FarmDiagnostics'));
const FarmActions = React.lazy(() => import('./pages/FarmActions'));
const FarmSetup = React.lazy(() => import('./pages/FarmSetup'));
const FarmCredentials = React.lazy(() => import('./pages/FarmCredentials'));
const PropertyIntake = React.lazy(() => import('./pages/PropertyIntake'));

function ProtectedRoute() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <EcointelLottie message="Signing you in..." lottiePixelWidth={140} lottiePixelHeight={175} />
      </div>
    );
  }
  return user ? <Outlet /> : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    const check = async () => {
      const u = auth.currentUser;
      if (!u) { setAllowed(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        setAllowed(snap.exists() && snap.data().role === ROLES.ADMIN);
      } catch {
        setAllowed(false);
      }
    };
    check();
  }, []);

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center h-full min-h-[40vh]">
        <EcointelLottie message="Checking access..." lottiePixelWidth={120} lottiePixelHeight={150} />
      </div>
    );
  }
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

// v0.3 Phase 3: redirect for the two retired in-app HTML report routes.
// Old bookmarks like /analytics/:farmId/report and
// /analytics/:farmId/report/land-health resolve to the new packages grid
// rather than 404. Uses useParams so the absolute path is built from the
// real :farmId — robust across react-router versions.
//
// v66.1: target promoted from `/analytics/:farmId/reports` (where the
// FarmReportsPage was originally mounted as a sub-route of analytics) to
// the new top-level `/reports/:farmId`. Reports is now a sibling of Map
// and Analytics in the per-property triad (sidebar in Layout.js exposes
// the same triad in the same order), so the URL shape matches the user's
// mental model. The legacy `/analytics/:farmId/reports` path is preserved
// below as its own redirect for any pre-v66.1 bookmarks.
function RetiredReportRedirect() {
  const { farmId } = useParams();
  return <Navigate to={`/reports/${farmId}`} replace />;
}

function SuperuserRoute({ children }) {
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    const check = async () => {
      const u = auth.currentUser;
      if (!u) { setAllowed(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        const role = snap.exists() ? snap.data().role : null;
        setAllowed(role === ROLES.ADMIN || role === ROLES.SUPERUSER);
      } catch {
        setAllowed(false);
      }
    };
    check();
  }, []);

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center h-full min-h-[40vh]">
        <EcointelLottie message="Checking access..." lottiePixelWidth={120} lottiePixelHeight={150} />
      </div>
    );
  }
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <Router>
      <CookieConsent />
      <BugHerdScript />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* Magic-link callback. Public route — Firebase Auth needs to complete
            the sign-in here before ProtectedRoute can see the user. See
            FRONTEND_MULTI_PROVIDER_AUTH_v1 §B. */}
        <Route path="/auth/email-link-callback" element={<EmailLinkCallback />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<StagingAccessGate />}>
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route element={<OnboardingGate />}>
            <Route element={<ViewComplexityProvider><FarmDataProvider><DemoModeProvider><Layout /></DemoModeProvider></FarmDataProvider></ViewComplexityProvider>}>
              {/* Routes accessible without active subscription */}
              <Route path="/checkout" element={<Subscription />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />

              {/* Routes gated behind subscription / admin override */}
              <Route element={<SubscriptionGate><Outlet /></SubscriptionGate>}>
                <Route path="/dashboard" element={<Dashboard />} />
                {/* Phase 2: new-user property intake (form posts to
                    POST /api/v1/properties/intake — see api.js → submitPropertyIntake).
                    Cap + plan checks are enforced both on the page and server-side. */}
                <Route path="/properties/new" element={<Suspense fallback={<EcoIntelFullBleed message="Loading intake form..." minHeight="min-h-[60vh]" />}><PropertyIntake /></Suspense>} />
                <Route path="/map/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading map..." minHeight="min-h-[70vh]" />}><MapViewer /></Suspense></DemoFarmRouteGuard>} />
                {/* Overview: per-property landing page (handoff 15), sibling of
                    Map / Analytics / Disclosure / Reports. Same shell + guards.
                    The 10-card property Overview reads useParams().farmId exactly
                    as the other property pages do. */}
                <Route path="/overview/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading overview..." minHeight="min-h-[70vh]" />}><FarmOverview /></Suspense></DemoFarmRouteGuard>} />
                {/* Assessments / Diagnostics / Actions (handoff 22, Type A): RB surface
                    blocks via useFarmSummary. Same shell + guards as Overview / Disclosure. */}
                <Route path="/assessments/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading assessments..." minHeight="min-h-[70vh]" />}><FarmAssessments /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/diagnostics/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading diagnostics..." minHeight="min-h-[70vh]" />}><FarmDiagnostics /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/actions/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading actions..." minHeight="min-h-[70vh]" />}><FarmActions /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/setup/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading setup..." minHeight="min-h-[70vh]" />}><FarmSetup /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/analytics/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading analytics..." minHeight="min-h-[70vh]" />}><FarmAnalytics /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/analytics/:farmId/field/:fieldName" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading field details..." minHeight="min-h-[70vh]" />}><FieldDetailsPage /></Suspense></DemoFarmRouteGuard>} />
                {/* Disclosure: fourth per-property page, sibling of Map / Analytics /
                    Reports. Same shell as /analytics/:farmId — DemoFarmRouteGuard gates
                    demo-farm access, Suspense + EcoIntelFullBleed while the lazy page
                    loads. The page reads useParams().farmId exactly as FarmAnalytics
                    does. Disclosure is informed-by, never an assurance statement. */}
                <Route path="/disclosure/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading disclosure..." minHeight="min-h-[70vh]" />}><FarmDisclosure /></Suspense></DemoFarmRouteGuard>} />
                {/* v66.1: Reports promoted to a top-level per-property route
                    so it sits as a sibling of Map and Analytics in the URL
                    space — matches the per-property triad in the sidebar
                    (Reports | Map | Analytics) and the new in-page nav
                    buttons in MapViewer/FarmAnalytics. The legacy
                    `/analytics/:farmId/reports` path is preserved as a
                    redirect below so bookmarks from the v0.3 Phase 3 era
                    still resolve. */}
                <Route path="/reports/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading reports..." minHeight="min-h-[70vh]" />}><FarmReportsPage /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/credentials/:farmId" element={<DemoFarmRouteGuard><Suspense fallback={<EcoIntelFullBleed message="Loading credentials..." minHeight="min-h-[70vh]" />}><FarmCredentials /></Suspense></DemoFarmRouteGuard>} />
                <Route path="/analytics/:farmId/reports" element={<RetiredReportRedirect />} />
                {/* v0.3 Phase 3: bookmarked deep-links into the retired
                    in-app HTML reports redirect into the new packages grid
                    (FarmReportsPage). Absolute-path redirect so it works
                    consistently regardless of react-router version. */}
                <Route path="/analytics/:farmId/report" element={<RetiredReportRedirect />} />
                <Route path="/analytics/:farmId/report/land-health" element={<RetiredReportRedirect />} />
                {/* Admin sub-routes — restructured from a single tabbed dashboard
                    into URL-routed sections so admins can deep-link, bookmark,
                    and find features as the console grows. The AdminRoute
                    guard remains the single entry point; AdminLayout owns the
                    grouped left sub-nav and shared {users, farms} payload. */}
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<AdminOverview />} />
                  <Route path="users" element={<UsersAdmin />} />
                  <Route path="properties" element={<PropertiesAdmin />} />
                  <Route path="reports" element={<ReportsAdmin />} />
                  <Route path="credentials" element={<CredentialsVettingAdmin />} />
                  <Route path="feedback" element={<FeedbackAdmin />} />
                  <Route path="announcements" element={<AnnouncementsAdmin />} />
                  <Route path="email-templates" element={<EmailTemplatesAdmin />} />
                  <Route path="promo-banner" element={<PromoBannerAdmin />} />
                  <Route path="site-content" element={<SiteContentAdmin />} />
                  <Route path="views" element={<ViewsAdmin />} />
                  <Route path="cache" element={<CacheAdmin />} />
                  <Route path="demo" element={<DemoAdmin />} />
                  <Route path="settings" element={<Navigate to="/admin/demo" replace />} />
                </Route>
                <Route path="/superuser" element={<Suspense fallback={<EcoIntelFullBleed message="Loading console..." minHeight="min-h-[60vh]" />}><SuperuserRoute><SuperuserDashboard /></SuperuserRoute></Suspense>} />
              </Route>

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>
          </Route>
        </Route>
      </Routes>
    </Router>
  );
}
